import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
} from "@/components/ui/drawer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import type { ExpandedSchedule } from "@/types/database/schedule";
import { useNavigate } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import Fuse from "fuse.js";
import {
    Infinity as InfinityIcon,
    Plus,
    Search,
    SortAsc,
    SortDesc,
} from "lucide-react";
import * as React from "react";
import type { Control, FieldValues, Path } from "react-hook-form";
import { useController } from "react-hook-form";

// Inline compact preview for schedule timings
function CompactSchedulePreview({ schedule }: { schedule: ExpandedSchedule }) {
    // Sort timings by their chronological order (smaller intervals first)
    const sortedTimings = React.useMemo(() => {
        const timeUnitValues: Record<string, number> = {
            second: 1,
            minute: 60,
            hour: 60 * 60,
            day: 24 * 60 * 60,
            week: 7 * 24 * 60 * 60,
            month: 30 * 24 * 60 * 60,
        };
        return [...schedule.timings].sort((a, b) =>
            (a.interval * (timeUnitValues[a.time_unit] || 0)) -
            (b.interval * (timeUnitValues[b.time_unit] || 0))
        );
    }, [schedule.timings]);

    function getOccurrenceLabel(timing, index) {
        if (timing.is_autoname) {
            return `${timing.time_unit} ${timing.interval * (index + 1)}`;
        }
        return timing.label;
    }

    interface PreviewItem {
        key: string;
        content: React.ReactNode;
    }
    const previewItems: PreviewItem[] = [];

    for (const timing of sortedTimings) {
        let content: React.ReactNode = null;
        if (timing.is_infinite) {
            // Infinite: show first 3, then ... infinity icon and text, all on one line
            const occurrences = [0, 1, 2].map((i) =>
                getOccurrenceLabel(timing, i)
            );
            content = (
                <span className="flex items-center gap-1">
                    <span className="inline-block bg-slate-400 rounded-full w-1 h-1" />
                    <span className="capitalize">
                        {occurrences.join(", ")}, ...
                    </span>
                    <InfinityIcon className="ml-1 w-3 h-3 text-indigo-500" />
                    <span className="ml-1 text-slate-500 text-xs">
                        repeats infinitely
                    </span>
                </span>
            );
        } else if (typeof timing.repeats === "number" && timing.repeats > 0) {
            if (timing.repeats <= 2) {
                // Show all occurrences (repeats + 1)
                const occurrences = Array.from(
                    { length: timing.repeats + 1 },
                    (_, i) => getOccurrenceLabel(timing, i),
                );
                content = (
                    <span className="flex items-center gap-1">
                        <span className="inline-block bg-slate-400 rounded-full w-1 h-1" />
                        <span className="capitalize">
                            {occurrences.join(", ")}
                        </span>
                    </span>
                );
            } else {
                // Finite repeats: show first 3, then ... last occurrence, all on one line
                const occurrences = [0, 1, 2].map((i) =>
                    getOccurrenceLabel(timing, i)
                );
                const lastLabel = getOccurrenceLabel(timing, timing.repeats);
                content = (
                    <span className="flex items-center gap-1">
                        <span className="inline-block bg-slate-400 rounded-full w-1 h-1" />
                        <span className="capitalize">
                            {occurrences.join(", ")}, ... {lastLabel}
                        </span>
                    </span>
                );
            }
        } else {
            // Single occurrence
            content = (
                <span className="flex items-center gap-1">
                    <span className="inline-block bg-slate-400 rounded-full w-1 h-1" />
                    <span className="capitalize">
                        {getOccurrenceLabel(timing, 0)}
                    </span>
                </span>
            );
        }
        previewItems.push({
            key: timing.timing_id,
            content,
        });
    }

    return (
        <ul className="space-y-0.5 pl-2 text-slate-600 text-xs">
            {previewItems.map((item) => <li key={item.key}>{item.content}</li>)}
        </ul>
    );
}

interface ScheduleSelectDialogProps<T extends FieldValues> {
    name: Path<T>;
    control: Control<T>;
    options: ExpandedSchedule[];
    addOption?: React.ReactNode;
    label?: string;
    description?: React.ReactNode;
    placeholder?: string;
    disabled?: boolean;
}

export function ScheduleSelectDialog<T extends FieldValues>({
    name,
    control,
    options,
    addOption,
    label,
    description,
    placeholder = "Select a schedule",
    disabled,
}: ScheduleSelectDialogProps<T>) {
    const {
        field: { value, onChange },
        fieldState: { error },
    } = useController({ name, control });
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const [alphaAsc, setAlphaAsc] = React.useState(true);
    const isMobile = useMediaQuery("(max-width: 640px)");
    const navigate = useNavigate();

    // Fuzzy search setup
    const fuse = React.useMemo(
        () =>
            new Fuse(options, {
                keys: ["schedule_name", "schedule_description"],
                threshold: 0.4,
            }),
        [options],
    );

    let filtered = search ? fuse.search(search).map((r) => r.item) : options;
    filtered = [...filtered].sort((a, b) =>
        alphaAsc
            ? a.schedule_name.localeCompare(b.schedule_name)
            : b.schedule_name.localeCompare(a.schedule_name)
    );

    function handleSelect(schedule_id: string) {
        onChange(schedule_id);
        setOpen(false);
    }

    const selected = options.find((o) => o.schedule_id === value);

    const Content = (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
                {label && <label className="font-medium text-sm">{label}
                </label>}
                {description && (
                    <span className="text-slate-500 text-xs">
                        {description}
                    </span>
                )}
            </div>
            <Button
                variant="outline"
                className={cn(
                    "h-12 px-3 text-sm w-full justify-between rounded-md bg-white text-left",
                    !selected && "text-slate-500",
                )}
                onClick={() => setOpen(true)}
                disabled={disabled}
            >
                <span className="text-sm truncate">
                    {selected
                        ? (
                            <span className="font-medium">
                                {selected.schedule_name}
                            </span>
                        )
                        : placeholder}
                </span>
                <Search className="ml-2 w-4 h-4" />
            </Button>
            {error && (
                <span className="text-red-500 text-xs">{error.message}</span>
            )}
        </div>
    );

    const OptionCard = (option: ExpandedSchedule) => (
        <button
            key={option.schedule_id}
            type="button"
            className={cn(
                "relative w-full text-left border rounded-lg p-3 flex flex-col gap-1 hover:bg-slate-50 dark:hover:bg-slate-900 transition",
                value === option.schedule_id &&
                    "border-indigo-600 ring-2 ring-indigo-100 shadow-sm bg-indigo-50/40",
            )}
            onClick={() => handleSelect(option.schedule_id)}
        >
            <div className="flex items-center gap-2">
                <span
                    className={cn(
                        "font-semibold text-sm",
                        value === option.schedule_id && "text-indigo-700",
                    )}
                >
                    {option.schedule_name}
                </span>
            </div>
            {option.schedule_description && (
                <div className="text-slate-500 text-xs">
                    {option.schedule_description}
                </div>
            )}
            <div className="mt-1">
                <CompactSchedulePreview schedule={option} />
            </div>
        </button>
    );

    const sortLabel = `Sort: ${alphaAsc ? "A-Z" : "Z-A"}`;
    const sortTooltip = `Sort alphabetically (${alphaAsc ? "A-Z" : "Z-A"})`;

    const List = (
        <>
            <div className="flex items-center gap-2 mb-2">
                <div className="relative flex-1">
                    <input
                        type="text"
                        placeholder="Search schedules..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full text-sm"
                    />
                    <Search className="top-1/2 right-2 absolute w-4 h-4 text-slate-500 -translate-y-1/2 pointer-events-none" />
                </div>
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setAlphaAsc((asc) => !asc)}
                        title={sortTooltip}
                        aria-label={sortTooltip}
                    >
                        {alphaAsc
                            ? <SortAsc className="w-5 h-5" />
                            : <SortDesc className="w-5 h-5" />}
                    </Button>
                    <span
                        className="min-w-[80px] text-slate-500 text-xs"
                        title={sortTooltip}
                    >
                        {sortLabel}
                    </span>
                </div>
            </div>
            <ScrollArea className="h-[50vh]">
                <div className="flex flex-col gap-2">
                    {filtered.length === 0 && (
                        <div className="py-8 text-slate-400 text-center">
                            No schedules found.
                        </div>
                    )}
                    {filtered.map(OptionCard)}
                </div>
            </ScrollArea>
            <div className="mt-2 pt-2 border-slate-200 border-t">
                <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-2 mt-2 w-full"
                    asChild
                >
                    <Link to="/public/schedule/request" target="_blank">
                        <Plus className="w-4 h-4" />
                        Request New Schedule
                    </Link>
                </Button>
            </div>
        </>
    );

    return (
        <div>
            {Content}
            {isMobile
                ? (
                    <Drawer open={open} onOpenChange={setOpen}>
                        <DrawerContent>
                            <DrawerHeader>
                                <DrawerTitle>
                                    Select a Schedule
                                </DrawerTitle>
                            </DrawerHeader>
                            <div className="p-4">{List}</div>
                        </DrawerContent>
                    </Drawer>
                )
                : (
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogContent className="max-w-lg">
                            <DialogHeader>
                                <DialogTitle>
                                    Select a Schedule
                                </DialogTitle>
                            </DialogHeader>
                            {List}
                        </DialogContent>
                    </Dialog>
                )}
        </div>
    );
}
